import { CameraIcon, PieChartIcon, ReceiptIcon, ShieldCheckIcon } from 'lucide-react'
import type { ProductPageContent } from '../../types/product'
import { industryCatalog, productCatalog } from './shared'

export const expenses: ProductPageContent = {
  slug: 'expenses',
  name: 'Expenses',
  icon: ReceiptIcon,
  hero: {
    headline: 'See exactly where your money goes',
    subheadline:
      'Record every business cost in seconds, keep receipts safe, and understand your spending by category, branch, and month.',
    trustLine: 'Receipts stored securely · Used by 5,800+ businesses',
    visual: {
      kind: 'devices',
      tablet: {
        module: 'Expenses',
        greeting: 'Expenses · September',
        kpis: [
          { label: 'Spent this month', value: 'RWF 3.4M', delta: { label: '−6% vs August', tone: 'positive' } },
          { label: 'Largest category', value: 'Stock' },
          { label: 'Awaiting receipt', value: '4', delta: { label: 'Add photos', tone: 'warning' } },
        ],
        main: {
          type: 'progress',
          title: 'By category',
          rows: [
            { label: 'Stock purchases', value: 'RWF 1.9M', pct: 56 },
            { label: 'Rent & utilities', value: 'RWF 720K', pct: 21 },
            { label: 'Transport & fuel', value: 'RWF 410K', pct: 12 },
            { label: 'Salaries', value: 'RWF 370K', pct: 11 },
          ],
        },
        side: {
          type: 'list',
          title: 'Recent',
          rows: [
            { label: 'Fuel · Delivery', value: 'RWF 25,000' },
            { label: 'Electricity', value: 'RWF 48,500' },
            { label: 'Packaging', value: 'RWF 32,000' },
          ],
        },
      },
      phone: {
        type: 'list',
        title: 'New expense',
        badge: { label: 'Receipt added', tone: 'positive' },
        rows: [
          { label: 'Category', value: 'Transport' },
          { label: 'Paid with', value: 'Mobile money' },
          { label: 'Branch', value: 'Remera' },
        ],
        footer: { label: 'Amount', value: 'RWF 25,000' },
        action: 'Save expense',
      },
    },
  },
  stats: [
    { value: '5,800+', label: 'businesses recording expenses with Kayko' },
    { value: '1.1M', label: 'receipts stored safely every month' },
    { value: '12%', label: 'average reduction in costs after one year' },
  ],
  benefits: {
    heading: 'Every cost, accounted for.',
    items: [
      { icon: CameraIcon, title: 'Record in seconds', copy: 'Snap a photo of the receipt, pick a category, and the expense is saved.' },
      { icon: PieChartIcon, title: 'Clear categories', copy: 'See spending by category, branch, and month without building a spreadsheet.' },
      { icon: ShieldCheckIcon, title: 'Ready for tax time', copy: 'Every receipt is stored with its expense, so your records are complete and easy to share.' },
    ],
  },
  features: {
    heading: 'Everything you need to control costs.',
    subheading: 'From daily fuel and airtime to rent, salaries, and supplier bills.',
    items: [
      {
        title: 'Capture expenses on the go',
        copy: 'Record an expense from your phone the moment you pay. Attach a photo of the receipt so nothing gets lost.',
        bullets: ['Receipt photos stored with every expense', 'Payment method and branch on each record', 'Works offline and syncs later'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Expense · 18 Sep',
            badge: { label: 'Receipt attached', tone: 'positive' },
            rows: [
              { label: 'Supplier', value: 'Kigali Packaging Ltd' },
              { label: 'Category', value: 'Packaging' },
              { label: 'Paid with', value: 'Bank transfer' },
              { label: 'Recorded by', value: 'Aline Uwimana' },
            ],
            footer: { label: 'Amount', value: 'RWF 32,000' },
          },
        },
      },
      {
        title: 'Organised by category',
        copy: 'Sort spending into categories that match your business. See at a glance where most of your money goes each month.',
        bullets: ['Custom categories and sub-categories', 'Spending by branch or project', 'Monthly budgets with alerts'],
        visual: {
          kind: 'card',
          card: {
            type: 'progress',
            title: 'September budget',
            meta: 'RWF 3,400,000 of RWF 4,000,000',
            rows: [
              { label: 'Stock purchases', value: '95%', pct: 95, tone: 'warning' },
              { label: 'Rent & utilities', value: '100%', pct: 100 },
              { label: 'Transport & fuel', value: '68%', pct: 68 },
              { label: 'Marketing', value: '40%', pct: 40 },
            ],
          },
        },
      },
      {
        title: 'Recurring bills and reminders',
        copy: 'Set up rent, salaries, and subscriptions once, and Kayko reminds you before each payment is due. No more late fees or forgotten bills.',
        bullets: ['Weekly, monthly, or yearly schedules', 'Reminders by notification or SMS', 'Mark paid in one tap'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Upcoming bills',
            rows: [
              { label: 'Shop rent · Remera', sublabel: 'Due 30 Sep', value: 'RWF 450,000', tone: 'warning' },
              { label: 'Staff salaries (5)', sublabel: 'Due 30 Sep', value: 'RWF 1,150,000' },
              { label: 'Internet', sublabel: 'Due 2 Oct', value: 'RWF 35,000' },
            ],
            footer: { label: 'Due this week', value: 'RWF 1,635,000' },
          },
        },
      },
      {
        title: 'Compare costs over time',
        copy: 'See how your spending changes month to month and spot costs that are creeping up. Share clean reports with your accountant.',
        bullets: ['Month-on-month comparisons', 'Export to spreadsheet or PDF', 'Expenses flow into profit reports'],
        visual: {
          kind: 'card',
          card: {
            type: 'bars',
            title: 'Monthly expenses',
            value: 'RWF 3.4M',
            delta: { label: '−6% vs August', tone: 'positive' },
            bars: [
              { label: 'Apr', value: 3.1 },
              { label: 'May', value: 3.5 },
              { label: 'Jun', value: 3.3 },
              { label: 'Jul', value: 3.8 },
              { label: 'Aug', value: 3.6 },
              { label: 'Sep', value: 3.4, highlight: true },
            ],
            legend: 'Transport costs fell 18% after route changes',
          },
        },
      },
    ],
  },
  steps: {
    heading: 'Up and running in an afternoon.',
    items: [
      { title: 'Set your categories', copy: 'Start with our suggested categories or create ones that fit your business.' },
      { title: 'Record as you spend', copy: 'Add expenses from your phone with a receipt photo, wherever you are.' },
      { title: 'Review every month', copy: 'See where money went and share a clean report with your accountant.' },
    ],
  },
  related: {
    heading: 'Works with the rest of Kayko.',
    subheading: 'Costs connect to your purchases, reports, and financing.',
    items: [productCatalog.purchases, productCatalog.reports, productCatalog['sales-pos'], productCatalog['business-finance']],
  },
  industries: {
    heading: 'Built for your industry.',
    subheading: 'Expenses fits every kind of business cost.',
    items: [industryCatalog.retail, industryCatalog.services, industryCatalog.hotels, industryCatalog.food],
  },
  testimonial: {
    quote:
      'I thought I knew what the shop cost to run. Once I recorded every expense in Kayko, I found money leaking on transport and fixed it within a month.',
    name: 'Job Gashangaza',
    role: 'Owner',
    business: 'Job’s Clothing Shop',
    location: 'Nyarugenge, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1f9a33f4-6198-4f9d-89dd-0aca07eb18bb.jpg',
    metric: { value: 'RWF 180K', label: 'saved every month on transport and packaging' },
  },
  pricingTeaser: { text: 'Expenses is included in every Kayko plan.', linkLabel: 'See pricing', href: '#' },
  faq: {
    heading: 'Frequently asked questions',
    items: [
      { question: 'Can my staff record expenses?', answer: 'Yes. Give staff permission to add expenses, and choose whether large amounts need your approval.' },
      { question: 'What happens to my receipt photos?', answer: 'They are stored securely with each expense and can be downloaded or shared with your accountant at any time.' },
      { question: 'Can I track expenses by branch?', answer: 'Yes. Assign each expense to a branch or project and compare spending across locations.' },
      { question: 'Does it help with tax filing?', answer: 'Kayko keeps complete, organised records of your costs with receipts attached, which makes preparing returns with your accountant much easier.' },
      { question: 'Can I set budgets?', answer: 'Yes. Set monthly budgets per category and get an alert when you are close to the limit.' },
      { question: 'Can I import past expenses?', answer: 'Yes. Upload past expenses from a spreadsheet so your reports include your full history.' },
    ],
  },
  cta: {
    headline: 'Get a clear view of your costs.',
    subheadline: 'Start recording expenses in minutes, with support from our team in Kigali.',
  },
}
